import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Palette, Type, Sparkles, Save, Plus, ChevronDown, Ban, Image as ImageIcon, Contact } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';
import ReportButton from '@/components/ReportButton';
import BusinessDescriptionPanel from '@/components/BusinessDescriptionPanel';

const DEFAULT_FORM = {
  business_name: '',
  industry: '',
  tone: '',
  colors: {
    primary: { hex: '#7C3AED' },
    secondary: { hex: '#06B6D4' },
    accent: { hex: '#D9F99D' },
    background: { hex: '#050505' },
    foreground: { hex: '#F8FAFC' },
  },
  typography: {
    heading_font: 'Poppins',
    body_font: 'Lora',
    mono_font: 'JetBrains Mono',
    heading_fallback: 'sans-serif',
    body_fallback: 'sans-serif',
  },
  logos: { primary: '', mono: '', reversed: '', icon: '', favicon: '' },
  forbidden_colors: [],
  visual_keywords: [],
  tone_of_voice: '',
};

const FONT_OPTIONS = [
  'Poppins', 'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat',
  'Playfair Display', 'Lora', 'Merriweather', 'Source Sans Pro',
  'DM Sans', 'Outfit', 'Space Grotesk', 'Work Sans'
];

const MONO_FONT_OPTIONS = [
  'JetBrains Mono', 'Space Mono', 'Fira Code', 'IBM Plex Mono', 'Roboto Mono'
];

const LOGO_SLOTS = [
  { key: 'primary', label: 'Primary (Full Color)' },
  { key: 'mono', label: 'Mono (Single Color)' },
  { key: 'reversed', label: 'Reversed (Light-on-Dark)' },
  { key: 'icon', label: 'Icon Only' },
  { key: 'favicon', label: 'Favicon' },
];

const BrandKitPanel = () => {
  const { currentProject, brandKit, saveBrandKit, loading, updateProject } = useProject();

  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [keywordInput, setKeywordInput] = useState('');
  const [forbiddenColorInput, setForbiddenColorInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [clientForm, setClientForm] = useState({ client_contact_name: '', client_email: '', client_phone: '', client_address: '' });
  const [clientHasChanges, setClientHasChanges] = useState(false);
  const [savingClient, setSavingClient] = useState(false);

  useEffect(() => {
    if (currentProject) {
      setClientForm({
        client_contact_name: currentProject.client_contact_name || '',
        client_email: currentProject.client_email || '',
        client_phone: currentProject.client_phone || '',
        client_address: currentProject.client_address || '',
      });
      setClientHasChanges(false);
    }
  }, [currentProject]);

  const handleClientChange = (field, value) => {
    setClientForm((prev) => ({ ...prev, [field]: value }));
    setClientHasChanges(true);
  };

  const handleSaveClient = async () => {
    try {
      setSavingClient(true);
      await updateProject(currentProject.id, clientForm);
      setClientHasChanges(false);
      toast.success('Client info saved');
    } catch (error) {
      toast.error('Failed to save client info');
    } finally {
      setSavingClient(false);
    }
  };

  useEffect(() => {
    if (brandKit) {
      setFormData({
        business_name: brandKit.business_name || currentProject?.name || '',
        industry: brandKit.industry || currentProject?.industry || '',
        tone: brandKit.tone || '',
        colors: {
          primary: brandKit.colors?.primary || DEFAULT_FORM.colors.primary,
          secondary: brandKit.colors?.secondary || DEFAULT_FORM.colors.secondary,
          accent: brandKit.colors?.accent || DEFAULT_FORM.colors.accent,
          background: brandKit.colors?.background || DEFAULT_FORM.colors.background,
          foreground: brandKit.colors?.foreground || DEFAULT_FORM.colors.foreground,
        },
        typography: {
          heading_font: brandKit.typography?.heading_font || 'Poppins',
          body_font: brandKit.typography?.body_font || 'Lora',
          mono_font: brandKit.typography?.mono_font || 'JetBrains Mono',
          heading_fallback: brandKit.typography?.heading_fallback || 'sans-serif',
          body_fallback: brandKit.typography?.body_fallback || 'sans-serif',
        },
        logos: {
          primary: brandKit.logos?.primary || '',
          mono: brandKit.logos?.mono || '',
          reversed: brandKit.logos?.reversed || '',
          icon: brandKit.logos?.icon || '',
          favicon: brandKit.logos?.favicon || '',
        },
        forbidden_colors: brandKit.forbidden_colors || [],
        visual_keywords: brandKit.visual_keywords || [],
        tone_of_voice: brandKit.tone_of_voice || ''
      });
    } else if (currentProject) {
      setFormData(prev => ({
        ...prev,
        business_name: currentProject.name || '',
        industry: currentProject.industry || ''
      }));
    }
  }, [brandKit, currentProject]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleColorChange = (role, hex) => {
    setFormData(prev => ({
      ...prev,
      colors: { ...prev.colors, [role]: { hex } }
    }));
    setHasChanges(true);
  };

  const handleTypographyChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      typography: {
        ...prev.typography,
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleLogoChange = (slot, url) => {
    setFormData(prev => ({
      ...prev,
      logos: { ...prev.logos, [slot]: url }
    }));
    setHasChanges(true);
  };

  const addTag = (field, input, setInput) => {
    const value = input.trim();
    if (value && !formData[field].includes(value)) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
      setInput('');
      setHasChanges(true);
    }
  };

  const removeTag = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(v => v !== value)
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await saveBrandKit(formData);
      setHasChanges(false);
      toast.success('Brand kit saved successfully!');
    } catch (error) {
      toast.error('Failed to save brand kit');
    }
  };

  return (
    <div className="h-full overflow-auto p-6" data-testid="brand-kit-panel">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-2xl text-foreground mb-2">Brand Kit</h2>
              <p className="text-muted-foreground">Define your brand's visual identity</p>
            </div>
            <div className="flex items-center gap-2">
              <ReportButton section="brand" projectId={currentProject?.id} />
              <Button
                onClick={handleSave}
                disabled={loading || !hasChanges}
                className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
                data-testid="save-brand-kit-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>

          <BusinessDescriptionPanel embedded />

          {/* Brand Identity Section */}
          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-heading text-lg text-foreground">Brand Identity</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-foreground/80">Business Name</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => handleChange('business_name', e.target.value)}
                  placeholder="Your business name"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="brand-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground/80">Industry</Label>
                <Input
                  value={formData.industry}
                  onChange={(e) => handleChange('industry', e.target.value)}
                  placeholder="e.g., Coffee Shop, Tech Startup"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="brand-industry-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground/80">Brand Tone</Label>
                <Input
                  value={formData.tone}
                  onChange={(e) => handleChange('tone', e.target.value)}
                  placeholder="e.g., Modern, Playful, Professional"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="brand-tone-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground/80">Tone of Voice</Label>
                <Input
                  value={formData.tone_of_voice}
                  onChange={(e) => handleChange('tone_of_voice', e.target.value)}
                  placeholder="e.g., Friendly, Professional, Witty"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="brand-voice-input"
                />
              </div>
            </div>
          </div>

          {/* Client Contact Section -- shown on the report header ("prepared for") */}
          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Contact className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-foreground">Client Contact</h3>
                  <p className="text-sm text-muted-foreground">Shown on the report header, not visible to the client themselves</p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={handleSaveClient}
                disabled={savingClient || !clientHasChanges}
                className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
                data-testid="save-client-contact-btn"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" />
                Save
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-foreground/80">Contact Name</Label>
                <Input
                  value={clientForm.client_contact_name}
                  onChange={(e) => handleClientChange('client_contact_name', e.target.value)}
                  placeholder="Jane Doe"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="client-contact-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/80">Email</Label>
                <Input
                  value={clientForm.client_email}
                  onChange={(e) => handleClientChange('client_email', e.target.value)}
                  placeholder="jane@client.com"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="client-contact-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/80">Phone</Label>
                <Input
                  value={clientForm.client_phone}
                  onChange={(e) => handleClientChange('client_phone', e.target.value)}
                  placeholder="+1 (555) 010-5678"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="client-contact-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/80">Address</Label>
                <Input
                  value={clientForm.client_address}
                  onChange={(e) => handleClientChange('client_address', e.target.value)}
                  placeholder="456 Client Ave, Sydney NSW"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="client-contact-address-input"
                />
              </div>
            </div>
          </div>

          {/* Color Palette Section */}
          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Palette className="w-5 h-5 text-secondary" />
              </div>
              <h3 className="font-heading text-lg text-foreground">Color Palette</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { role: 'primary', label: 'Primary Color' },
                { role: 'secondary', label: 'Secondary Color' },
                { role: 'accent', label: 'Accent Color' },
              ].map(({ role, label }) => (
                <div className="space-y-3" key={role}>
                  <Label className="text-foreground/80">{label}</Label>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-14 h-14 rounded-xl border border-black/15 cursor-pointer transition-transform hover:scale-105"
                      style={{ backgroundColor: formData.colors[role]?.hex }}
                    >
                      <input
                        type="color"
                        value={formData.colors[role]?.hex || '#000000'}
                        onChange={(e) => handleColorChange(role, e.target.value)}
                        className="w-full h-full opacity-0 cursor-pointer"
                        data-testid={`${role}-color-picker`}
                      />
                    </div>
                    <Input
                      value={formData.colors[role]?.hex || ''}
                      onChange={(e) => handleColorChange(role, e.target.value)}
                      className="bg-black/[0.03] border-border text-foreground font-mono text-sm"
                      data-testid={`${role}-color-input`}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Color Preview */}
            <div className="mt-6 p-4 rounded-xl bg-black/[0.03] border border-white/[0.08]">
              <p className="text-xs text-muted-foreground mb-3">Preview</p>
              <div className="flex gap-2">
                <div className="flex-1 h-20 rounded-lg" style={{ backgroundColor: formData.colors.primary?.hex }} />
                <div className="flex-1 h-20 rounded-lg" style={{ backgroundColor: formData.colors.secondary?.hex }} />
                <div className="flex-1 h-20 rounded-lg" style={{ backgroundColor: formData.colors.accent?.hex }} />
              </div>
            </div>
          </div>

          {/* Typography Section */}
          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                <Type className="w-5 h-5 text-accent" />
              </div>
              <h3 className="font-heading text-lg text-foreground">Typography</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-foreground/80">Heading Font</Label>
                <select
                  value={formData.typography.heading_font}
                  onChange={(e) => handleTypographyChange('heading_font', e.target.value)}
                  className="w-full h-10 px-3 rounded-lg bg-black/[0.03] border border-border text-foreground"
                  data-testid="heading-font-select"
                >
                  {FONT_OPTIONS.map(font => (
                    <option key={font} value={font} className="bg-card">{font}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground/80">Body Font</Label>
                <select
                  value={formData.typography.body_font}
                  onChange={(e) => handleTypographyChange('body_font', e.target.value)}
                  className="w-full h-10 px-3 rounded-lg bg-black/[0.03] border border-border text-foreground"
                  data-testid="body-font-select"
                >
                  {FONT_OPTIONS.map(font => (
                    <option key={font} value={font} className="bg-card">{font}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground/80">Mono Font (Optional)</Label>
                <select
                  value={formData.typography.mono_font || ''}
                  onChange={(e) => handleTypographyChange('mono_font', e.target.value || null)}
                  className="w-full h-10 px-3 rounded-lg bg-black/[0.03] border border-border text-foreground"
                  data-testid="mono-font-select"
                >
                  <option value="" className="bg-card">None</option>
                  {MONO_FONT_OPTIONS.map(font => (
                    <option key={font} value={font} className="bg-card">{font}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Typography Preview */}
            <div className="mt-6 p-4 rounded-xl bg-black/[0.03] border border-white/[0.08]">
              <p className="text-xs text-muted-foreground mb-3">Preview</p>
              <p
                className="text-3xl text-foreground mb-2"
                style={{ fontFamily: formData.typography.heading_font }}
              >
                {formData.business_name || 'Your Brand Name'}
              </p>
              <p
                className="text-base text-muted-foreground mb-2"
                style={{ fontFamily: formData.typography.body_font }}
              >
                Body text will look like this. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              </p>
              {formData.typography.mono_font && (
                <p
                  className="text-sm text-muted-foreground"
                  style={{ fontFamily: formData.typography.mono_font }}
                >
                  Mono sample / brand-kit-v1.2.0
                </p>
              )}
            </div>
          </div>

          {/* Advanced Section */}
          <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced} className="mb-6">
            <div className="glass rounded-2xl border border-border overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  className="w-full flex items-center justify-between p-6 text-left"
                  data-testid="advanced-toggle"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-black/[0.03] flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-foreground/80" />
                    </div>
                    <h3 className="font-heading text-lg text-foreground">Advanced: Background, Logos</h3>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-muted-foreground transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-6 pb-6 space-y-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-4">Background / Foreground</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {[
                        { role: 'background', label: 'Background Color' },
                        { role: 'foreground', label: 'Foreground (Text) Color' },
                      ].map(({ role, label }) => (
                        <div className="space-y-3" key={role}>
                          <Label className="text-foreground/80">{label}</Label>
                          <div className="flex items-center gap-3">
                            <div
                              className="w-14 h-14 rounded-xl border border-black/15 cursor-pointer transition-transform hover:scale-105"
                              style={{ backgroundColor: formData.colors[role]?.hex }}
                            >
                              <input
                                type="color"
                                value={formData.colors[role]?.hex || '#000000'}
                                onChange={(e) => handleColorChange(role, e.target.value)}
                                className="w-full h-full opacity-0 cursor-pointer"
                                data-testid={`${role}-color-picker`}
                              />
                            </div>
                            <Input
                              value={formData.colors[role]?.hex || ''}
                              onChange={(e) => handleColorChange(role, e.target.value)}
                              className="bg-black/[0.03] border-border text-foreground font-mono text-sm"
                              data-testid={`${role}-color-input`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground mb-4">Logo Variants (URL)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {LOGO_SLOTS.map(({ key, label }) => (
                        <div className="space-y-2" key={key}>
                          <Label className="text-foreground/80">{label}</Label>
                          <Input
                            value={formData.logos[key]}
                            onChange={(e) => handleLogoChange(key, e.target.value)}
                            placeholder="https://..."
                            className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                            data-testid={`logo-${key}-input`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Forbidden Colors Section */}
          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <Ban className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="font-heading text-lg text-foreground">Forbidden Colors</h3>
            </div>

            <div className="flex gap-2 mb-4">
              <Input
                value={forbiddenColorInput}
                onChange={(e) => setForbiddenColorInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag('forbidden_colors', forbiddenColorInput, setForbiddenColorInput))}
                placeholder="Add a hex code to avoid (e.g., #FF0000)"
                className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                data-testid="forbidden-color-input"
              />
              <Button
                onClick={() => addTag('forbidden_colors', forbiddenColorInput, setForbiddenColorInput)}
                variant="outline"
                className="bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
                data-testid="add-forbidden-color-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {formData.forbidden_colors.map((color, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 rounded-full bg-black/[0.03] border border-border text-sm text-foreground flex items-center gap-2 group"
                >
                  <span
                    className="w-3 h-3 rounded-full border border-black/15"
                    style={{ backgroundColor: color }}
                  />
                  {color}
                  <button
                    onClick={() => removeTag('forbidden_colors', color)}
                    className="text-muted-foreground hover:text-red-400 transition-colors"
                    data-testid={`remove-forbidden-color-${index}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              {formData.forbidden_colors.length === 0 && (
                <p className="text-sm text-muted-foreground">No forbidden colors added yet</p>
              )}
            </div>
          </div>

          {/* Visual Keywords Section */}
          <div className="glass rounded-2xl p-6 border border-border">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-heading text-lg text-foreground">Visual Keywords</h3>
            </div>

            <div className="flex gap-2 mb-4">
              <Input
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag('visual_keywords', keywordInput, setKeywordInput))}
                placeholder="Add a keyword (e.g., modern, organic)"
                className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                data-testid="keyword-input"
              />
              <Button
                onClick={() => addTag('visual_keywords', keywordInput, setKeywordInput)}
                variant="outline"
                className="bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
                data-testid="add-keyword-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {formData.visual_keywords.map((keyword, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 rounded-full bg-black/[0.03] border border-border text-sm text-foreground flex items-center gap-2 group"
                >
                  {keyword}
                  <button
                    onClick={() => removeTag('visual_keywords', keyword)}
                    className="text-muted-foreground hover:text-red-400 transition-colors"
                    data-testid={`remove-keyword-${index}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              {formData.visual_keywords.length === 0 && (
                <p className="text-sm text-muted-foreground">No keywords added yet</p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BrandKitPanel;
